import {Component, computed, ElementRef, OnDestroy, OnInit, signal, Signal, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Location} from '@angular/common';
import {CommonModule} from '@angular/common';
import {SignalServerService} from '../../services/signal-server.service';
import {ContextService} from '../../services/context.service';
import {Organization} from '../../types-repository';
import {BreadCrumb, LogoAreaComponent} from '../logo-area/logo-area.component';
import {HeadlineSectionComponent} from '../headline-section/headline-section.component';

@Component({
  selector: 'app-desktop-viewer',
  templateUrl: './desktop-viewer.component.html',
  styleUrls: ['./desktop-viewer.component.scss'],
  imports: [CommonModule, LogoAreaComponent, HeadlineSectionComponent],
})
export class DesktopViewerComponent implements OnInit, OnDestroy {
  @ViewChild('remoteVideo', {static: true}) remoteVideoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('remoteAudio', {static: true}) remoteAudioRef!: ElementRef<HTMLAudioElement>;
  @ViewChild('viewerContainer', {static: true}) containerRef!: ElementRef<HTMLDivElement>;

  status = signal<string>('Connecting...');
  connected = signal(false);
  hostName = signal('');
  monitor = signal(0);
  password = signal<string | null>(null);
  showPassword = signal(false);
  error = signal<string | null>(null);

  private pc: RTCPeerConnection | null = null;
  private dataChannel: RTCDataChannel | null = null;
  private connectedToPeerId = -1;
  private hostInstanceId = '';
  private session = '';
  private isFullscreen = false;
  private lastEscapeTime = 0;
  private pingInterval: any = null;
  private statsInterval: any = null;
  private connectAttempts = 0;
  private static readonly MAX_CONNECT_ATTEMPTS = 3;

  // Modifier key state tracking (synced on mouse events)
  private altKeyPressed = false;
  private ctrlKeyPressed = false;
  private metaKeyPressed = false;
  private shiftKeyPressed = false;

  organization: Signal<Organization>;
  breadCrumbs: Signal<BreadCrumb[]>;

  constructor(
    private route: ActivatedRoute,
    private location: Location,
    private signalServer: SignalServerService,
    private contextService: ContextService,
  ) {
    this.organization = this.contextService.organization;
    this.breadCrumbs = computed(() => [
      {display: 'Content Platform', commands: ['/o', this.organization()?.id]},
      {display: 'Remote Desktop', commands: ['/o', this.organization()?.id, 'crd']},
    ]);
  }

  ngOnInit() {
    document.addEventListener('fullscreenchange', () => {
      this.isFullscreen = !!document.fullscreenElement;
      this.sendDC(this.isFullscreen ? 'mkon' : 'mkff');
    });
    this.hostInstanceId = this.route.snapshot.paramMap.get('hostId') || '';
    const q = this.route.snapshot.queryParamMap;
    const signalServerUrl = q.get('signalServerUrl') || '';
    const session = q.get('session') || '';
    const hostPeerId = parseInt(q.get('hostPeerId') || '0', 10);
    const clientPeerId = parseInt(q.get('clientPeerId') || '0', 10);
    const monitor = parseInt(q.get('monitor') || '0', 10);
    this.monitor.set(monitor);
    this.hostName.set(q.get('hostName') || this.hostInstanceId);
    this.session = session;
    this.password.set(q.get('password') || null);
    this.signalServer.configure(signalServerUrl, session, clientPeerId);
    this.status.set('Connecting...');
    this.connect(hostPeerId);
  }

  ngOnDestroy() {
    this.disconnect();
  }

  private async connect(hostPeerId: number | null) {
    this.error.set(null);
    try {
      if (!hostPeerId) {
        this.error.set('Host is not registered on signal server. Try Bootstrap Host first.');
        return;
      }

      this.connectedToPeerId = hostPeerId;
      this.status.set(`Connecting to ${this.hostName()}...`);

      this.pc = new RTCPeerConnection({
        iceServers: [
          {urls: ['stun:stun.l.google.com:19302']},
          {urls: ['stun:stun2.l.google.com:19302']},
        ],
      });

      this.pc.onicecandidate = (event) => {
        if (event.candidate) {
          console.log(`[WebRTC] ICE candidate: ${event.candidate.type} ${event.candidate.protocol} ${event.candidate.address}:${event.candidate.port}`);
          this.signalServer.sendMessage(this.connectedToPeerId, JSON.stringify(event.candidate));
        } else {
          console.log('[WebRTC] ICE gathering complete');
        }
      };

      this.pc.onicecandidateerror = (event) => {
        console.warn(`[WebRTC] ICE candidate error: ${event.errorCode} ${event.errorText} (${event.url})`);
      };

      this.pc.oniceconnectionstatechange = () => {
        console.log(`[WebRTC] ICE connection state: ${this.pc?.iceConnectionState}`);
      };

      this.pc.onicegatheringstatechange = () => {
        console.log(`[WebRTC] ICE gathering state: ${this.pc?.iceGatheringState}`);
      };

      // Retry: if not connected after 15s, tear down and rebuild the offer (max 3 attempts)
      const connectTimeout = setTimeout(() => {
        if (!this.connected()) {
          const iceState = this.pc?.iceConnectionState;
          const connState = this.pc?.connectionState;
          console.log(`[WebRTC] Timeout — iceState=${iceState} connState=${connState}`);
          this.connectAttempts++;
          if (this.connectAttempts >= DesktopViewerComponent.MAX_CONNECT_ATTEMPTS) {
            console.log('[WebRTC] Max connection attempts reached, giving up');
            const hint = iceState === 'checking'
              ? 'ICE negotiation timed out — the host may be unreachable (check security group UDP rules or add a TURN server).'
              : iceState === 'failed'
                ? 'ICE failed — no viable network path found between browser and host.'
                : 'No response from host binary — it may have crashed or cannot reach the signal server.';
            this.error.set(`Could not connect to ${this.hostName()}. ${hint}`);
            this.pc?.close();
            this.pc = null;
            return;
          }
          console.log(`[WebRTC] Connection timeout, retrying (attempt ${this.connectAttempts + 1}/${DesktopViewerComponent.MAX_CONNECT_ATTEMPTS})...`);
          this.status.set(`Retrying connection (${this.connectAttempts + 1}/${DesktopViewerComponent.MAX_CONNECT_ATTEMPTS})...`);
          this.pc?.close();
          this.pc = null;
          this.connect(hostPeerId);
        }
      }, 15000);

      this.pc.onconnectionstatechange = () => {
        const state = this.pc?.connectionState;
        console.log(`[WebRTC] Connection state: ${state}`);
        if (state === 'connected') {
          clearTimeout(connectTimeout);
          this.status.set(`Connected to ${this.hostName()}`);
          this.connected.set(true);
          this.error.set(null);
          this.containerRef.nativeElement.focus();
          this.sendDC('mkon');
          // Log stats periodically
          this.startStatsLogging();
        } else if (state === 'disconnected' || state === 'failed') {
          clearTimeout(connectTimeout);
          console.log(`[WebRTC] Connection lost — state: ${state}`);
          this.status.set('Disconnected');
          this.connected.set(false);
          this.error.set('Connection lost');
        }
      };

      this.pc.addTransceiver('video', {direction: 'recvonly'});
      this.pc.addTransceiver('audio', {direction: 'recvonly'});

      this.pc.ontrack = (event) => {
        console.log(`[WebRTC] ontrack: kind=${event.track.kind} id=${event.track.id}`);
        if (event.track.kind === 'video') {
          const stream = new MediaStream([event.track]);
          const video = this.remoteVideoRef.nativeElement;
          video.srcObject = stream;
          video.play().catch((e) => console.warn('[WebRTC] video play() rejected:', e));
        } else if (event.track.kind === 'audio') {
          const audio = this.remoteAudioRef.nativeElement;
          audio.srcObject = new MediaStream([event.track]);
          audio.play().catch((e) => {
            console.warn('[WebRTC] audio play() blocked by autoplay policy:', e);
            // Browser blocked autoplay — resume on next user interaction
            const resume = () => {
              audio.play().catch((e2) => console.error('[WebRTC] audio play() retry failed:', e2));
              document.removeEventListener('click', resume);
              document.removeEventListener('keydown', resume);
            };
            document.addEventListener('click', resume, {once: true});
            document.addEventListener('keydown', resume, {once: true});
          });
        }
      };

      this.dataChannel = this.pc.createDataChannel('mk', {ordered: true});
      this.dataChannel.onopen = () => {
        this.pingInterval = setInterval(() => {
          if (this.dataChannel?.readyState === 'open') {
            this.dataChannel.send('ping ' + Date.now());
          }
        }, 5000);
      };
      this.dataChannel.onclose = () => {
        if (this.pingInterval) clearInterval(this.pingInterval);
      };
      this.dataChannel.onmessage = (event) => {
        const msg = event.data as string;
        if (msg.startsWith('moim')) {
          const video = this.remoteVideoRef.nativeElement;
          const text = msg.substring(5);
          if (text) {
            const parts = text.split(' ');
            const kscale = video.videoWidth / video.offsetWidth;
            const xshift = parseInt(parts[0]) / kscale;
            const yshift = parseInt(parts[1]) / kscale;
            const base64 = parts[2];
            video.style.cursor = `-webkit-image-set(url(${base64}) ${kscale}x) ${xshift} ${yshift}, auto`;
          } else {
            video.style.cursor = 'none';
          }
        } else if (msg.startsWith('clpa ')) {
          navigator.clipboard.writeText(msg.substring(5)).catch(() => {});
        }
      };

      this.pc.onnegotiationneeded = async () => {
        try {
          const offer = await this.pc!.createOffer();
          await this.pc!.setLocalDescription(offer);
          const ld = this.pc!.localDescription!;
          this.signalServer.sendMessage(this.connectedToPeerId, JSON.stringify({sdp: ld.sdp, type: ld.type}));
        } catch (e) {
          console.error('Negotiation failed:', e);
        }
      };

      this.signalServer.startPolling(async (data) => {
        if (!this.pc) return;
        if (data === 'BYE') { this.disconnect(); return; }

        try {
          const msg = JSON.parse(data);
          if (msg.type === 'offer' || msg.type === 'answer') {
            await this.pc.setRemoteDescription(new RTCSessionDescription(msg));
            if (msg.type === 'offer') {
              const answer = await this.pc.createAnswer();
              await this.pc.setLocalDescription(answer);
              this.signalServer.sendMessage(this.connectedToPeerId, JSON.stringify(this.pc.localDescription));
            }
          } else if (msg.candidate !== undefined) {
            await this.pc.addIceCandidate(new RTCIceCandidate(msg));
          }
        } catch (e) {
          console.error('[WebRTC] Error processing signal message:', e);
        }
      });
    } catch (e: any) {
      this.error.set(`Connection failed: ${e.message}`);
    }
  }

  private startStatsLogging() {
    if (this.statsInterval) clearInterval(this.statsInterval);
    this.statsInterval = setInterval(async () => {
      if (!this.pc) return;
      try {
        const audio = this.remoteAudioRef.nativeElement;
        console.log(`[Audio] element: paused=${audio.paused} muted=${audio.muted} volume=${audio.volume}`);
        const audioTrack = (audio.srcObject as MediaStream)?.getAudioTracks()[0];
        if (audioTrack) {
          console.log(`[Audio] track: enabled=${audioTrack.enabled} muted=${audioTrack.muted} state=${audioTrack.readyState}`);
        }

        const stats = await this.pc.getStats();
        stats.forEach((report: any) => {
          if (report.type === 'candidate-pair' && report.state === 'succeeded') {
            console.log(`[WebRTC] Stats: rtt=${report.currentRoundTripTime}s bytes=${report.bytesReceived} nominated=${report.nominated}`);
          }
          if (report.type === 'inbound-rtp' && report.kind === 'audio') {
            console.log(`[Audio] inbound-rtp: bytesReceived=${report.bytesReceived} audioLevel=${report.audioLevel?.toFixed(4)} totalAudioEnergy=${report.totalAudioEnergy?.toFixed(4)} packetsLost=${report.packetsLost}`);
          }
        });
      } catch { /* ignore */ }
    }, 5000);
  }

  disconnect() {
    if (this.pingInterval) clearInterval(this.pingInterval);
    if (this.statsInterval) clearInterval(this.statsInterval);
    this.signalServer.stopPolling();
    if (this.connectedToPeerId > 0) {
      this.signalServer.sendMessage(this.connectedToPeerId, 'BYE');
    }
    this.dataChannel?.close();
    this.pc?.close();
    this.pc = null;
    this.connected.set(false);
  }

  retry() {
    this.disconnect();
    this.error.set(null);
    this.connectAttempts = 0;
    this.connect(this.connectedToPeerId);
  }

  goBack() {
    this.disconnect();
    this.location.back();
  }

  toggleFullscreen() {
    const container = this.remoteVideoRef.nativeElement.parentElement!;
    if (!document.fullscreenElement) {
      container.requestFullscreen?.();
      this.sendDC('mkon');
    } else {
      document.exitFullscreen?.();
      this.sendDC('mkff');
    }
  }

  onPaste(event: ClipboardEvent) {
    const text = event.clipboardData?.getData('text');
    if (text) {
      this.sendDC('clpa ' + text);
      event.preventDefault();
    }
  }

  // --- Input forwarding using the old string-based protocol ---

  private sendDC(msg: string) {
    if (this.dataChannel?.readyState === 'open') {
      this.dataChannel.send(msg);
    }
  }

  /** Convert mouse event to absolute host pixel coordinates, accounting for letterboxing */
  private getHostCoords(event: MouseEvent): {x: number, y: number} {
    const video = this.remoteVideoRef.nativeElement;
    if (!video.videoWidth || !video.videoHeight) return {x: 0, y: 0};

    // Calculate the actual video content area within the element (excluding letterbox)
    const videoRatio = video.videoWidth / video.videoHeight;
    let contentWidth = video.offsetWidth, contentHeight = video.offsetHeight;
    const elementRatio = contentWidth / contentHeight;
    if (elementRatio > videoRatio) {
      contentWidth = contentHeight * videoRatio;
    } else {
      contentHeight = contentWidth / videoRatio;
    }
    const letterboxLeft = (video.offsetWidth - contentWidth) / 2;
    const letterboxTop = (video.offsetHeight - contentHeight) / 2;

    // Get mouse position relative to the video element
    const rect = video.getBoundingClientRect();
    const relX = event.clientX - rect.left;
    const relY = event.clientY - rect.top;

    // Map from element-relative coords (minus letterbox) to host resolution
    const x = Math.round((relX - letterboxLeft) * video.videoWidth / contentWidth) + 1;
    const y = Math.round((relY - letterboxTop) * video.videoHeight / contentHeight) + 1;
    return {x, y};
  }

  /** Sync modifier key state — the OS may intercept key events */
  private syncModifiers(event: MouseEvent) {
    if (event.altKey !== this.altKeyPressed) {
      this.sendDC(event.altKey ? 'kewn 18' : 'keup 18');
      this.altKeyPressed = event.altKey;
    }
    if (event.ctrlKey !== this.ctrlKeyPressed) {
      this.sendDC(event.ctrlKey ? 'kewn 17' : 'keup 17');
      this.ctrlKeyPressed = event.ctrlKey;
    }
    if (event.metaKey !== this.metaKeyPressed) {
      this.sendDC(event.metaKey ? 'kewn 91' : 'keup 91');
      this.metaKeyPressed = event.metaKey;
    }
    if (event.shiftKey !== this.shiftKeyPressed) {
      this.sendDC(event.shiftKey ? 'kewn 16' : 'keup 16');
      this.shiftKeyPressed = event.shiftKey;
    }
  }

  onMouseEvent(event: MouseEvent) {
    if (!this.dataChannel || this.dataChannel.readyState !== 'open') return;
    const {x, y} = this.getHostCoords(event);
    this.syncModifiers(event);

    // Command format: first 2 chars + last 2 chars of event type
    // mousemove → "move", mouseup → "moup", mousedown → "mown"
    const type = event.type.slice(0, 2) + event.type.slice(-2);
    this.sendDC(`${type} ${x} ${y} ${event.buttons}`);
    event.preventDefault();
  }

  onKeyEvent(event: KeyboardEvent) {
    if (!this.dataChannel || this.dataChannel.readyState !== 'open') return;
    // Double-Escape exits fullscreen (single Escape is forwarded to Windows)
    if (event.key === 'Escape' && this.isFullscreen && event.type === 'keydown') {
      const now = Date.now();
      if (now - this.lastEscapeTime < 500) {
        document.exitFullscreen?.();
        this.lastEscapeTime = 0;
        return;
      }
      this.lastEscapeTime = now;
    }
    // On Ctrl+V: read browser clipboard, sync to remote, then forward the keystroke.
    // (preventDefault on keydown suppresses the paste event, so onPaste() never fires for Ctrl+V.)
    if (event.type === 'keydown' && event.keyCode === 86 && event.ctrlKey && navigator.clipboard?.readText) {
      event.preventDefault();
      event.stopPropagation();
      navigator.clipboard.readText().then(text => {
        if (text) this.sendDC('clpa ' + text);
        this.sendDC('kewn 86 v');
      }).catch(() => {
        this.sendDC('kewn 86 v'); // forward keystroke even if clipboard read is denied
      });
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const type = event.type === 'keydown' ? 'kewn' : 'keup';
    // keyCode is deprecated but the host binary expects it
    this.sendDC(`${type} ${event.keyCode} ${event.key}`);
  }

  onWheel(event: WheelEvent) {
    if (!this.dataChannel || this.dataChannel.readyState !== 'open') return;
    event.preventDefault();
    this.sendDC(`whel ${Math.round(event.deltaX)} ${Math.round(event.deltaY)}`);
  }
}
